import React, { useEffect, useRef, useState } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { fetchManageAccessData, updatePageAccess } from '../../../services/manageAccess';
import { Loading } from '../../Common/OtherElements/Loading';
import { handleErrors } from '../../../utils/errorHandler';
import { usePageLevelAccess } from '../../../hooks/usePageLevelAccess';

const ACCESS_FIELDS = [
    { key: 'viewAccess', label: 'View' },
    { key: 'addAccess', label: 'Add' },
    { key: 'editAccess', label: 'Edit' },
    { key: 'deleteAccess', label: 'Delete' },
    { key: 'downloadAccess', label: 'Download' },
];

const allAccessPatch = (value) =>
    ACCESS_FIELDS.reduce((patch, field) => {
        patch[field.key] = value;
        return patch;
    }, {});

const fieldState = (pages, key) => {
    const onCount = pages.filter((page) => page[key]).length;
    return {
        checked: pages.length > 0 && onCount === pages.length,
        mixed: onCount > 0 && onCount < pages.length,
    };
};

const groupPages = (pages) => {
    const grouped = new Map();

    pages.forEach((page) => {
        const name = page.pageGroupName || 'Other';
        if (!grouped.has(name)) {
            grouped.set(name, []);
        }
        grouped.get(name).push(page);
    });

    return Array.from(grouped.entries())
        .map(([name, items]) => ({
            name,
            pages: [...items].sort((a, b) => a.pageId - b.pageId),
        }))
        .sort(
            (a, b) =>
                Math.min(...a.pages.map((page) => page.pageId)) -
                Math.min(...b.pages.map((page) => page.pageId))
        );
};

const AccessSwitch = ({ id, checked, mixed, disabled, onChange }) => {
    const inputRef = useRef(null);

    useEffect(() => {
        if (inputRef.current) {
            inputRef.current.indeterminate = Boolean(mixed);
        }
    }, [mixed, checked]);

    return (
        <div className={`form-check form-switch form-switch-custom form-switch-primary access-switch${mixed ? ' is-mixed' : ''} mb-0`}>
            <input
                ref={inputRef}
                className="form-check-input"
                type="checkbox"
                role="switch"
                id={id}
                checked={checked}
                disabled={disabled}
                aria-checked={mixed ? 'mixed' : checked}
                onChange={(event) => onChange(event.target.checked)}
            />
        </div>
    );
};

export const ManageAccessContent = () => {
    const { roleId } = useParams();
    const [accessData, setAccessData] = useState([]);
    const [loading, setLoading] = useState(true);
    const [searchTerm, setSearchTerm] = useState('');
    const [collapsedGroups, setCollapsedGroups] = useState({});
    const [savingIds, setSavingIds] = useState([]);
    const PageLevelAccessurl = 'Manage-role-access/:roleId/:roleName';
    const navigate = useNavigate();
    const { pageAccessData } = usePageLevelAccess(PageLevelAccessurl);

    useEffect(() => {
        if (pageAccessData && !pageAccessData.viewAccess) {
            navigate('/400-error-page');
        }
    }, [pageAccessData, navigate]);

    useEffect(() => {
        const getData = async () => {
            try {
                const data = await fetchManageAccessData(roleId);
                setAccessData(data);
            } catch (error) {
                handleErrors(error);
            } finally {
                setLoading(false);
            }
        };

        getData();
    }, [roleId]);

    const handleBulkAccess = async (pages, patch) => {
        const updates = pages
            .map((page) => ({ ...page, ...patch }))
            .filter((next, index) =>
                ACCESS_FIELDS.some((field) => next[field.key] !== pages[index][field.key])
            );

        if (!updates.length) return;

        const ids = updates.map((item) => item.pageId);
        setSavingIds((prev) => [...new Set([...prev, ...ids])]);

        try {
            await Promise.all(updates.map((item) => updatePageAccess(item)));
            setAccessData((prevData) =>
                prevData.map((item) => updates.find((updated) => updated.pageId === item.pageId) || item)
            );
        } catch (error) {
            handleErrors(error);
        } finally {
            setSavingIds((prev) => prev.filter((id) => !ids.includes(id)));
        }
    };

    const toggleGroup = (name) => {
        setCollapsedGroups((prev) => ({ ...prev, [name]: !prev[name] }));
    };

    const filteredData = accessData.filter((item) => {
        const searchLower = searchTerm.toLowerCase();
        return (
            (item.pageGroupName || '').toLowerCase().includes(searchLower) ||
            (item.pageName || '').toLowerCase().includes(searchLower) ||
            (item.pageLink || '').toLowerCase().includes(searchLower)
        );
    });

    const groups = groupPages(filteredData);

    return (
        <div className="row">
            <div className="col-xxl-12">
                <div className="card mt-xxl-n5">
                    <div className="card-header role-access-toolbar">
                        <div>
                            <h5 className="mb-1">Manage Role Access</h5>
                            <p className="text-muted mb-0 fs-13">
                                Each menu is its own section. Switch a whole group, one action, or a single page.
                            </p>
                        </div>
                        <div className="role-access-search-wrap">
                            <i className="ri-search-line" />
                            <input
                                type="text"
                                placeholder="Search group or page..."
                                className="form-control"
                                value={searchTerm}
                                onChange={(e) => setSearchTerm(e.target.value)}
                            />
                        </div>
                    </div>
                    <div className="card-body">
                        {loading ? (
                            <Loading />
                        ) : groups.length > 0 ? (
                            groups.map((group) => {
                                const collapsed = Boolean(collapsedGroups[group.name]);
                                const groupSaving = group.pages.some((page) => savingIds.includes(page.pageId));
                                const fullyOnCount = group.pages.filter((page) =>
                                    ACCESS_FIELDS.every((field) => page[field.key])
                                ).length;
                                const everyPermissionOn = fullyOnCount === group.pages.length;
                                const somePermissionOn = group.pages.some((page) =>
                                    ACCESS_FIELDS.some((field) => page[field.key])
                                );

                                return (
                                    <div className="role-access-group" key={group.name}>
                                        <div className="role-access-group-header">
                                            <button
                                                type="button"
                                                className="role-access-group-title"
                                                onClick={() => toggleGroup(group.name)}
                                            >
                                                <i className={collapsed ? 'ri-arrow-right-s-line' : 'ri-arrow-down-s-line'} />
                                                <span>{group.name}</span>
                                                <span className="role-access-group-meta">
                                                    {fullyOnCount}/{group.pages.length}
                                                </span>
                                            </button>
                                            <div className="role-access-group-actions">
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light"
                                                    disabled={groupSaving || everyPermissionOn}
                                                    onClick={() => handleBulkAccess(group.pages, allAccessPatch(true))}
                                                >
                                                    All on
                                                </button>
                                                <button
                                                    type="button"
                                                    className="btn btn-sm btn-light"
                                                    disabled={groupSaving || !somePermissionOn}
                                                    onClick={() => handleBulkAccess(group.pages, allAccessPatch(false))}
                                                >
                                                    All off
                                                </button>
                                            </div>
                                        </div>

                                        {!collapsed && (
                                            <div className="table-responsive">
                                                <table className="table align-middle mb-0 role-access-table">
                                                    <thead>
                                                        <tr>
                                                            <th>Page</th>
                                                            {ACCESS_FIELDS.map((field) => (
                                                                <th key={field.key} className="role-access-col">{field.label}</th>
                                                            ))}
                                                        </tr>
                                                    </thead>
                                                    <tbody>
                                                        <tr className="role-access-all-row">
                                                            <td>
                                                                <div className="role-access-page">
                                                                    <span className="role-access-page-name">Entire group</span>
                                                                    <span className="role-access-link">Every page in {group.name}</span>
                                                                </div>
                                                            </td>
                                                            {ACCESS_FIELDS.map((field) => {
                                                                const state = fieldState(group.pages, field.key);
                                                                return (
                                                                    <td key={field.key} className="role-access-col">
                                                                        <AccessSwitch
                                                                            id={`${group.name}-${field.key}-all`}
                                                                            checked={state.checked}
                                                                            mixed={state.mixed}
                                                                            disabled={groupSaving}
                                                                            onChange={(value) =>
                                                                                handleBulkAccess(group.pages, { [field.key]: value })
                                                                            }
                                                                        />
                                                                    </td>
                                                                );
                                                            })}
                                                        </tr>
                                                        {group.pages.map((page) => {
                                                            const pageSaving = savingIds.includes(page.pageId);

                                                            return (
                                                                <tr key={page.pageId} className={pageSaving ? 'is-saving' : undefined}>
                                                                    <td>
                                                                        <div className="role-access-page">
                                                                            <span className="role-access-page-name">{page.pageName}</span>
                                                                            <span className="role-access-link">{page.pageLink}</span>
                                                                        </div>
                                                                    </td>
                                                                    {ACCESS_FIELDS.map((field) => (
                                                                        <td key={field.key} className="role-access-col">
                                                                            <AccessSwitch
                                                                                id={`${page.pageId}-${field.key}`}
                                                                                checked={Boolean(page[field.key])}
                                                                                disabled={pageSaving}
                                                                                onChange={(value) =>
                                                                                    handleBulkAccess([page], { [field.key]: value })
                                                                                }
                                                                            />
                                                                        </td>
                                                                    ))}
                                                                </tr>
                                                            );
                                                        })}
                                                    </tbody>
                                                </table>
                                            </div>
                                        )}
                                    </div>
                                );
                            })
                        ) : (
                            <div className="text-center text-muted py-4">No pages found.</div>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};
